import { Fade } from "@mui/material";
import {
  always,
  any,
  chain,
  curryN,
  defaultTo,
  filter,
  find,
  forEach,
  fromPairs,
  groupBy,
  ifElse,
  join,
  map,
  mapObjIndexed,
  mergeRight,
  nth,
  path,
  pipe,
  prop,
  propEq,
  reject,
  tap,
  toPairs,
  toString,
  uniq,
} from "ramda";
import { useCallback } from "react";
import { defineMessages } from "react-intl";
import { ERROR_CODES } from "../../features/Authorization/fetchError.utils";
import { ErrorBoundary } from "../ErrorBoundary";
import { useMsg } from "../Msg/Msg";
import { validationMessages } from "./validationMessages";
import * as validations from "./validations";

// <|>
// https://github.com/ramda/ramda/issues/1279
const isValidateName = curryN(2, (validateName, parametrizedValidateItem) =>
  propEq(validateName, 0, parametrizedValidateItem),
);

export const getValidateParamsMaybe = curryN(
  2,
  (validateName, parametrizedValidate = []) =>
    pipe(
      find(isValidateName(validateName)),
      defaultTo([]),
      nth(1),
    )(parametrizedValidate),
);

const getValidate = (validateName, parametrizedValidate) =>
  pipe(
    defaultTo([]),
    find(isValidateName(validateName)),
    defaultTo([]),
  )(parametrizedValidate);

// for dynamic rules revalidation, see triggerRevalidationToken
export const getRHFRules = ({
  rules = {},
  parametrizedValidate: _parametrizedValidate = [],
  debug,
}) => {
  try {
    const parametrizedValidate = filter(Boolean, _parametrizedValidate);
    // tricky, let's trust to native validation for now
    // also rules.required is used to default clearable and disableClearable
    const isRequired = any(isValidateName("required"), parametrizedValidate);

    const rulesResult = {
      ...rules,
      ...(isRequired ? { required: true } : { required: false }), // without required: false field stays required when rules change
      validate: mergeRight(
        rules.validate || {},
        pipe(
          reject(isValidateName("required")), // TODO: {hasValue: (value) => ...} arg by input type?
          map(([key, params]) => [key, validations[key](params)]),
          fromPairs,
        )(parametrizedValidate),
      ),
    };
    if (debug)
      console.log("[getRHFRules]", debug, {
        isRequired,
        rules,
        parametrizedValidate,
        rulesResult,
      });

    return rulesResult;
  } catch (e) {
    debugger;
    return {};
  }
};

export const SERVER_ERROR_TYPE = "serverErrorType";

const FieldErrorInner = ({
  field,
  fieldState,
  rules,
  parametrizedValidate,
  customError,
  debug,
}) => {
  const msg = useMsg({ dict: validationMessages });
  const errorType = fieldState.error?.type; // rules.validate & parametrizedValidate

  if (!errorType) {
    if (customError) return <span>{customError}</span>;
    else return null;
  }

  let errorText = "";
  if (errorType === SERVER_ERROR_TYPE) {
    // Server error message is translated when response is received
    // fieldState.error of type FieldError from react-hook-form is limited to message of type string
    // so if we have translation for field errorCode, we translate, if not, we pass english message from the server
    errorText = fieldState.error?.message || "Server error";
  } else {
    // defaultParams needed to pass params from deprecated rules to intl message
    // e.g. rules: { minLength: 5 } instead of parametrizedValidate: ["minLength", { gteLength: 1 }]
    // TODO: use new parametrizedValidate everywhere instead of rules and remove this logic
    const defaultParams = {
      gteLength: rules?.minLength || "Unknown",
    };
    const [, params = defaultParams, optionsMaybe] = getValidate(
      errorType,
      parametrizedValidate,
    );
    const tsKey = optionsMaybe?.tsKey || `dict.validation.${errorType}`;
    const tsParams = map(toString, params); // rePattern -> regexpToMatch throws when passed to react-intl

    errorText = msg.maybe(tsKey, tsParams) || `Error of type: ${errorType}`;
  }
  // if (true) { error?.ref?.focus?.(); error?.ref?.select?.(); }
  if (debug) {
    // debugger;
    console.log("[FieldErrorInner.rndr]", {
      fieldState,
      rules,
      parametrizedValidate,
      customError,
      errorText,
    });
  }

  // Fade in to prevent error blink when revalidating
  return (
    <Fade in={true} timeout={500}>
      <span>{errorText}</span>
    </Fade>
  );
};

export const FieldError = (props) => {
  return (
    <ErrorBoundary>
      <FieldErrorInner {...props} />
    </ErrorBoundary>
  );
};

const createGetServerFieldErrorPairs =
  (translateError = ({ message, errorCode }) => message || errorCode) =>
  (jsonMaybe) =>
    pipe(
      filter(path(["fields", "length"])),
      chain((err) =>
        err.fields.map(({ name, value }) => ({
          fieldName: name,
          message: err.errorMessage,
          errorCode: err.errorCode || "Unknown error code",
        })),
      ),
      groupBy(prop("fieldName")),
      mapObjIndexed(pipe(map(translateError), uniq, join(". "))),
      toPairs,
    )(jsonMaybe);

const serverFieldErrorMessages = defineMessages({
  [`dict.serverFieldError.${ERROR_CODES.NOT_NULL}`]: {
    id: "dict.serverFieldError.NotNull",
    defaultMessage: "Required field",
  },
  [`dict.serverFieldError.${ERROR_CODES.NOT_EMPTY}`]: {
    id: "dict.serverFieldError.NotEmpty",
    defaultMessage: "Required field",
  },
});

const log = (msg) => tap(console.log.bind(console, msg));

export const useServerFieldErrors = ({ form }) => {
  const msg = useMsg({ dict: serverFieldErrorMessages });

  const translateError = useCallback(
    ({ fieldName, errorCode, message }) => {
      const tsKey = `dict.serverFieldError.${errorCode}`;
      if (!Object.values(ERROR_CODES).includes(errorCode)) {
        console.warn(
          `[useServerFieldErrors] Missing errorCode mapping for field "${fieldName}": ${errorCode}`,
        );
      }
      const translatedMessage =
        msg.maybe(tsKey) || message || `Error code: ${errorCode || "Unknown"}`;
      return translatedMessage;
    },
    [msg],
  );

  const { setError } = form;
  const getServerFieldErrorPairs = useCallback(
    (jsonMaybe) =>
      ifElse(
        Array.isArray,
        createGetServerFieldErrorPairs(translateError),
        pipe(log("[getServerFieldErrorPairs] expected array"), always([])),
      )(jsonMaybe),
    [translateError],
  );
  const setServerFieldErrors = useCallback(
    (jsonMaybe) => {
      pipe(
        getServerFieldErrorPairs,
        forEach(([fieldName, message]) => {
          // https://react-hook-form.com/docs/useform/seterror
          setError(fieldName, { type: SERVER_ERROR_TYPE, message });
        }),
      )(jsonMaybe);
    },
    [getServerFieldErrorPairs, setError],
  );

  return {
    getServerFieldErrorPairs,
    setServerFieldErrors,
  };
};
